import { UNSAVED_EVENTS_KEY } from '@/constants';
import { onAppHidden } from '@shared/lib/app-lifecycle';

import { subscribe } from '../bus';
import type { ReaderEvent } from '../track';
import { eventLogDb, type EventLogRow } from './event-log-db';

/**
 * The durable log of reader events: the source of truth on the device for
 * 30 days. Every recorded event is appended, whatever the reader's sharing
 * setting and whether or not the tenant has a destination; those only
 * decide what is *sent*. Consumers read it by `seq` with a cursor each.
 *
 * Writes are memory-first: an event waits in a buffer and is written a
 * moment later in one transaction with whatever else arrived, and at once
 * when the page is hidden (the last moment a page is sure to run).
 *
 * A database write started while the page is closing is not sure to finish:
 * the browser may tear the page down first. So what is unwritten at that
 * moment is also put, synchronously, in `localStorage` (`UNSAVED_EVENTS_KEY`),
 * and the next launch writes it to the log. Without this the reader's last
 * reading before a reload or a closed tab was never recorded.
 */

/** Raw events are kept this long, by the event's own time. */
export const EVENT_LOG_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * A second limit, whichever is reached first. Device storage belongs to the
 * reader and their downloaded issues need it more: at roughly 600 bytes an
 * event this is several thousand events, more than a month of heavy reading.
 */
export const EVENT_LOG_BYTE_BUDGET = 3 * 1024 * 1024;
/** How long an event may wait in memory before it is written. */
const FLUSH_DELAY_MS = 1000;
const SWEEP_INTERVAL_MS = 60 * 60 * 1000;
/** Without a database, the newest events of this page are kept in memory. */
const MEMORY_LIMIT = 2000;

export type LoggedEvent = Required<Pick<EventLogRow, 'seq'>> & {
	event: ReaderEvent;
	shared: boolean;
};

let pending: Array<Omit<EventLogRow, 'seq'>> = [];
// Set by the analytics consumer, which owns the sharing setting. Until it
// is, nothing counts as shared: an event must never be sent by mistake.
let isSharingOn: () => boolean = () => false;
const memoryCursors = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let flushing: Promise<void> | undefined;
let dbUsable = true;
// The whole log, when the database cannot be used (private mode, quota, a
// corrupted store): consumers still work for this page, nothing survives it.
let memory: Array<LoggedEvent> = [];
let memorySeq = 0;
let lastSweepAt = 0;
let knownBytes: number | undefined;
let started: (() => void) | undefined;
const listeners = new Set<() => void>();

function notify() {
	for (const listener of listeners) {
		try {
			listener();
		} catch {
			// One consumer failing must not stop the others.
		}
	}
}

function toMemory(rows: Array<Omit<EventLogRow, 'seq'>>) {
	for (const row of rows) {
		memorySeq += 1;
		memory.push({ seq: memorySeq, event: row.event, shared: row.shared });
	}
	if (memory.length > MEMORY_LIMIT) {
		memory = memory.slice(-MEMORY_LIMIT);
	}
}

function toRow(event: ReaderEvent): Omit<EventLogRow, 'seq'> {
	return {
		event_ts: event.event_ts,
		bytes: JSON.stringify(event).length,
		// Decided now, when it happened, not when it is written or sent.
		shared: isSharingOn(),
		event,
	};
}

/** Whether the reader is sharing analytics right now, as the analytics consumer reports it. */
export function isSharingNow(): boolean {
	return isSharingOn();
}

/** Who decides whether an event recorded now may ever be sent. */
export function configureEventLog(options: { isSharingOn: () => boolean }): void {
	isSharingOn = options.isSharingOn;
}

type PendingRow = Omit<EventLogRow, 'seq'>;
// Ids of the events currently in `localStorage`, so the common case (none)
// costs nothing.
const unsaved = new Set<string>();

function readUnsaved(): Array<PendingRow> {
	try {
		const stored: unknown = JSON.parse(
			localStorage.getItem(UNSAVED_EVENTS_KEY) ?? '[]',
		);
		return Array.isArray(stored)
			? (stored as Array<PendingRow>).filter(
					(row) => typeof row?.event?.event_id === 'string',
				)
			: [];
	} catch {
		return [];
	}
}

/** Keeps `rows` where a closing page cannot lose them. Synchronous on purpose. */
function keepUnsaved(rows: Array<PendingRow>): void {
	const fresh = rows.filter((row) => !unsaved.has(row.event.event_id));
	if (!fresh.length) {
		return;
	}
	try {
		localStorage.setItem(
			UNSAVED_EVENTS_KEY,
			// The newest few: on a device whose database never works, this
			// must not grow with every page that closes.
			JSON.stringify([...readUnsaved(), ...fresh].slice(-UNSAVED_LIMIT)),
		);
		for (const row of fresh) {
			unsaved.add(row.event.event_id);
		}
	} catch {
		// No storage, or it is full: the database write is the only chance.
	}
}

/** They are in the log now. */
function dropUnsaved(rows: Array<PendingRow>): void {
	if (!unsaved.size) {
		return;
	}
	const written = new Set(rows.map((row) => row.event.event_id));
	try {
		const left = readUnsaved().filter((row) => !written.has(row.event.event_id));
		if (left.length) {
			localStorage.setItem(UNSAVED_EVENTS_KEY, JSON.stringify(left));
		} else {
			localStorage.removeItem(UNSAVED_EVENTS_KEY);
		}
	} catch {
		// Left behind: the next launch finds them already in the log.
	}
	for (const id of written) {
		unsaved.delete(id);
	}
}

const UNSAVED_LIMIT = 100;
// How far back the next launch looks for an event that did reach the log
// after all (the write can finish although the page was closing).
const RECOVERY_LOOKBACK = 500;

/**
 * Writes to the log what an earlier page left in `localStorage`, unless it
 * is there already. With its own `event_ts` and its own sharing stamp, like
 * any other row; only its place in the log (`seq`) is later than it would
 * have been.
 */
async function recoverUnsaved(): Promise<void> {
	const rows = readUnsaved();
	if (!rows.length) {
		return;
	}
	for (const row of rows) {
		unsaved.add(row.event.event_id);
	}
	let known = new Set<string>();
	try {
		const recent = await eventLogDb.events
			.orderBy('seq')
			.reverse()
			.limit(RECOVERY_LOOKBACK)
			.toArray();
		known = new Set(recent.map((row) => row.event.event_id));
	} catch {
		// No usable database: they go to this page's memory log.
	}
	const missing = rows.filter((row) => !known.has(row.event.event_id));
	dropUnsaved(rows.filter((row) => known.has(row.event.event_id)));
	if (missing.length) {
		// Ahead of anything this page has recorded: they happened first.
		pending = [...missing, ...pending];
		await flushEventLog();
	}
}

async function write(): Promise<void> {
	while (pending.length) {
		const events = pending;
		pending = [];
		if (!dbUsable) {
			toMemory(events);
			notify();
			continue;
		}
		try {
			const rows = events;
			await eventLogDb.events.bulkAdd(rows);
			dropUnsaved(rows);
			if (knownBytes !== undefined) {
				knownBytes += rows.reduce((sum, row) => sum + row.bytes, 0);
			}
			await evict();
		} catch {
			// From here on this page logs to memory. The database's own rows
			// stay where they are for a later launch.
			dbUsable = false;
			memorySeq = await lastSeq();
			toMemory(events);
		}
		notify();
	}
}

async function lastSeq(): Promise<number> {
	try {
		return (await eventLogDb.events.orderBy('seq').last())?.seq ?? 0;
	} catch {
		return 0;
	}
}

/** Writes whatever is buffered. Resolves when it is in the log. */
export function flushEventLog(): Promise<void> {
	if (flushTimer !== undefined) {
		clearTimeout(flushTimer);
		flushTimer = undefined;
	}
	// Queued behind a write that is still finishing, never merged into it:
	// that one may already have looked at the buffer and found it empty (the
	// log's own flush on `pagehide`, a tick before the reader records its
	// last reading), and this event would then be written by nobody.
	const run = (flushing ?? Promise.resolve()).then(write);
	const tracked: Promise<void> = run.finally(() => {
		if (flushing === tracked) {
			flushing = undefined;
		}
	});
	flushing = tracked;
	return tracked;
}

// True from `pagehide` until the page is shown again (it can come back from
// the back/forward cache).
let leaving = false;

/**
 * The page is hidden or on its way out: there may be no "a moment later".
 * What is recorded now is exactly what the page going away produces (the
 * reader's last reading is closed by the same `visibilitychange` or
 * `pagehide` that flushes this log), so it must not wait for the timer.
 */
function isGoingAway(): boolean {
	return (
		leaving ||
		(typeof document !== 'undefined' && document.visibilityState === 'hidden')
	);
}

/** Appends an event. Returns at once; the write follows. */
export function appendToEventLog(event: ReaderEvent): void {
	const row = toRow(event);
	pending.push(row);
	if (isGoingAway()) {
		// Whichever listener ran first, the log's flush or the one that
		// recorded this event, the event is written now; and kept where a
		// closing page cannot lose it, in case that write does not finish.
		keepUnsaved([row]);
		void flushEventLog();
		return;
	}
	flushTimer ??= setTimeout(() => void flushEventLog(), FLUSH_DELAY_MS);
}

/**
 * Drops events older than 30 days, then the oldest ones while the log is
 * over its byte budget. Age is checked at most once an hour; the budget
 * after every write, since that is the only time the log grows.
 */
export async function evict(now: number = Date.now()): Promise<void> {
	if (now - lastSweepAt >= SWEEP_INTERVAL_MS) {
		lastSweepAt = now;
		const expired = await eventLogDb.events
			.where('event_ts')
			.below(now - EVENT_LOG_TTL_MS)
			.delete();
		if (expired) {
			knownBytes = undefined;
		}
	}

	// Summed from the table once per page, then kept up to date by `write`:
	// a full scan after every event would cost more than the log is worth.
	if (knownBytes === undefined) {
		let sum = 0;
		await eventLogDb.events.each((row) => {
			sum += row.bytes;
		});
		knownBytes = sum;
	}
	if (knownBytes <= EVENT_LOG_BYTE_BUDGET) {
		return;
	}
	let total = knownBytes;
	const doomed: Array<number> = [];
	await eventLogDb.events
		.orderBy('seq')
		.until(() => total <= EVENT_LOG_BYTE_BUDGET)
		.each((row) => {
			total -= row.bytes;
			doomed.push(row.seq as number);
		});
	await eventLogDb.events.bulkDelete(doomed);
	knownBytes = total;
}

/**
 * Up to `limit` events recorded after `seq`, oldest first. An evicted event
 * is simply not there: a consumer that fell behind the retained window
 * resumes at the oldest event that is left.
 */
export async function readEventLog(
	afterSeq: number,
	limit = 100,
): Promise<Array<LoggedEvent>> {
	if (dbUsable) {
		try {
			const rows = await eventLogDb.events
				.where('seq')
				.above(afterSeq)
				.limit(limit)
				.toArray();
			return rows.map((row) => ({
				seq: row.seq as number,
				event: row.event,
				shared: row.shared,
			}));
		} catch {
			dbUsable = false;
		}
	}
	return memory.filter((row) => row.seq > afterSeq).slice(0, limit);
}

/**
 * A consumer's place in the log: the `seq` of the last event it dealt with,
 * or `undefined` if it has never run (a new destination starts at the
 * oldest event still kept, which is how it is backfilled).
 */
export async function readCursor(consumer: string): Promise<number | undefined> {
	if (dbUsable) {
		try {
			const stored = (await eventLogDb.cursors.get(consumer))?.seq;
			return stored ?? memoryCursors.get(consumer);
		} catch {
			dbUsable = false;
		}
	}
	return memoryCursors.get(consumer);
}

export async function writeCursor(consumer: string, seq: number): Promise<void> {
	memoryCursors.set(consumer, seq);
	if (!dbUsable) {
		return;
	}
	try {
		await eventLogDb.cursors.put({ consumer, seq });
	} catch {
		// Kept in memory for this page.
	}
}

/** Called after every write, so a consumer knows there is something to read. */
export function onEventLogAppend(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * Starts recording: from now on everything `track()` publishes is appended.
 * Call once at boot; calling it again does nothing.
 */
export function startEventLog(): void {
	if (started) {
		return;
	}
	const unsubscribe = subscribe(appendToEventLog);
	const offHidden = onAppHidden(() => {
		// What was recorded in the last moment and is still in the buffer.
		keepUnsaved(pending);
		void flushEventLog();
	});
	void recoverUnsaved();
	const onPageHide = () => {
		leaving = true;
	};
	const onPageShow = () => {
		leaving = false;
	};
	window.addEventListener('pagehide', onPageHide);
	window.addEventListener('pageshow', onPageShow);
	started = () => {
		unsubscribe();
		offHidden();
		window.removeEventListener('pagehide', onPageHide);
		window.removeEventListener('pageshow', onPageShow);
	};
}

/** Empties the log and forgets every cursor (the reader cleared their data). */
export async function clearEventLog(): Promise<void> {
	pending = [];
	unsaved.clear();
	try {
		localStorage.removeItem(UNSAVED_EVENTS_KEY);
	} catch {
		// Nothing was kept there.
	}
	memory = [];
	memoryCursors.clear();
	knownBytes = undefined;
	try {
		await eventLogDb.transaction(
			'rw',
			eventLogDb.events,
			eventLogDb.cursors,
			async () => {
				await eventLogDb.events.clear();
				await eventLogDb.cursors.clear();
			},
		);
	} catch {
		// Nothing durable to clear.
	}
}

/** Test-only: stop recording and forget everything held in memory. */
export async function resetEventLogForTests(): Promise<void> {
	started?.();
	started = undefined;
	leaving = false;
	unsaved.clear();
	if (flushTimer !== undefined) {
		clearTimeout(flushTimer);
		flushTimer = undefined;
	}
	await flushing;
	pending = [];
	memory = [];
	memorySeq = 0;
	lastSweepAt = 0;
	knownBytes = undefined;
	dbUsable = true;
	isSharingOn = () => false;
	memoryCursors.clear();
	listeners.clear();
}
